import BiMap from 'ts-bidirectional-map';
import {
  GeometricAlgorithmType,
  MeshAlgorithmType,
  PolylineAlgorithmType,
  MultiPolygonAlgorithmType,
  MultiPolygon2AlgorithmType,
  Polyline2AlgorithmType,
  ExtrudedPolygonAlgorithmType,
  PointAlgorithmType,
  Point2AlgorithmType,
} from './algorithm-types';
import { GeometricProperty, LocationProperty } from './geometric-types';

export enum IfcClass {
  CurtainWall = 'IfcCurtainWall',
  Door = 'IfcDoor',
  Covering = 'IfcCovering',
  Roof = 'IfcRoof',
  Space = 'IfcSpace',
  Slab = 'IfcSlab',
  Proxy = 'IfcBuildingElementProxy',
  Part = 'IfcBuildingElementPart',
  Wall = 'IfcWall',
  WallStandardCase = 'IfcWallStandardCase',
  Window = 'IfcWindow',
  Member = 'IfcMember',
  Column = 'IfcColumn',
  Beam = 'IfcBeam',
  ElementAssembly = 'IfcElementAssembly',
  Footing = 'IfcFooting',
  Pile = 'IfcPile',
  PipeSegment = 'IfcPipeSegment',
  PipeFitting = 'IfcPipeFitting',
}

export enum RevitCategory {
  Wall = 'Revit Walls',
  Window = 'Revit Windows',
  Door = 'Revit Doors',
  Slab = 'Revit Floors',
  Column = 'Revit Columns',
  StructuralColumn = 'Revit Structural Columns',
  Ceiling = 'Revit Ceilings',
  Beam = 'Revit Beams',
  StructuralBeam = 'Revit Structural Beams',
  StructuralMember = 'Revit Structural Members',
  StructuralFraming = 'Revit Structural Framing',
  Space = 'Revit Spaces',
  Roof = 'Revit Roofs',
  CurtainWall = 'Revit Curtain Walls',
  Covering = 'Revit Coverings',
  Member = 'Revit Members',
  Furniture = 'Revit Furniture',
  Plants = 'Revit Planting',
  Stairs = 'Revit Stairs',
  Railing = 'Revit Railings',
  Parts = 'Parts',
}

export enum CustomElementClass {
  PolygonShape = 'PolygonShape',
  ExtrudedPolygonShape = 'ExtrudedPolygonShape',
  LineShape = 'LineShape',
  PointShape = 'PointShape',
  Polygon2Shape = 'Polygon2Shape',
  Line2Shape = 'Line2Shape',
  Point2Shape = 'Point2Shape',
}

export type AlgorithmTargetType = IfcClass | RevitCategory | CustomElementClass;

const mapToBiMap = (
  pairs: [GeometricProperty | LocationProperty, GeometricAlgorithmType][]
) => {
  let biMap = new BiMap<
    GeometricProperty | LocationProperty,
    GeometricAlgorithmType
  >();
  for (const [key, value] of pairs) {
    biMap.set(key, value);
  }
  return biMap;
};

// Default mapping for any IfcClass or RevitCategory that doesn't have a specific mapping
const DEFAULT_MESH_MAPPING = mapToBiMap([
  [LocationProperty.LOCATION_MIN_X, MeshAlgorithmType.MESH_LOCATION_MIN_X],
  [LocationProperty.LOCATION_MIN_Y, MeshAlgorithmType.MESH_LOCATION_MIN_Y],
  [LocationProperty.LOCATION_MIN_Z, MeshAlgorithmType.MESH_LOCATION_MIN_Z],
  [LocationProperty.LOCATION_MAX_X, MeshAlgorithmType.MESH_LOCATION_MAX_X],
  [LocationProperty.LOCATION_MAX_Y, MeshAlgorithmType.MESH_LOCATION_MAX_Y],
  [LocationProperty.LOCATION_MAX_Z, MeshAlgorithmType.MESH_LOCATION_MAX_Z],
]);

// For each each algorithm target type (ifcclass or shapes) there is a 1-1 relationship between geometric property types and geometric algorithm types
export const GEOMETRIC_PROPERTY_TO_ALGORITHM_MAP = new Map<
  AlgorithmTargetType,
  BiMap<GeometricProperty | LocationProperty, GeometricAlgorithmType>
>([
  [
    IfcClass.Wall,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.WallStandardCase,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.CurtainWall,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.Door,
    mapToBiMap([
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.Covering,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.Roof,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
      [GeometricProperty.TOP_SURFACE_AREA, MeshAlgorithmType.TOP_SURFACE_AREA],
      [
        GeometricProperty.BOTTOM_SURFACE_AREA,
        MeshAlgorithmType.BOTTOM_SURFACE_AREA,
      ],
    ]),
  ],
  [
    IfcClass.Space,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [GeometricProperty.TOP_SURFACE_AREA, MeshAlgorithmType.TOP_SURFACE_AREA],
      [
        GeometricProperty.BOTTOM_SURFACE_AREA,
        MeshAlgorithmType.BOTTOM_SURFACE_AREA,
      ],
      [GeometricProperty.TOTAL_SURFACE_AREA, MeshAlgorithmType.TOTAL_MESH_AREA],
    ]),
  ],
  [
    IfcClass.Slab,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.LARGEST_SURFACE_AREA_WITHOUT_OPENINGS,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.Proxy,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.Window,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.Member,
    mapToBiMap([
      [GeometricProperty.LENGTH, MeshAlgorithmType.BOUNDING_BOX_LENGTH],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY,
      ],
    ]),
  ],
  [
    IfcClass.Column,
    mapToBiMap([
      [GeometricProperty.LENGTH, MeshAlgorithmType.BOUNDING_BOX_LENGTH],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.HEIGHT,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
    ]),
  ],
  [
    IfcClass.Beam,
    mapToBiMap([
      [GeometricProperty.LENGTH, MeshAlgorithmType.BOUNDING_BOX_LENGTH],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.Footing,
    mapToBiMap([
      [GeometricProperty.LENGTH, MeshAlgorithmType.BOUNDING_BOX_LENGTH],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
    ]),
  ],
  [
    IfcClass.Pile,
    mapToBiMap([
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.ElementAssembly,
    mapToBiMap([
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    IfcClass.PipeSegment,
    mapToBiMap([
      [GeometricProperty.LENGTH, MeshAlgorithmType.BOUNDING_BOX_LENGTH],
      [
        GeometricProperty.DIAMETER,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
    ]),
  ],
  [
    IfcClass.PipeFitting,
    mapToBiMap([
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    CustomElementClass.PolygonShape,
    mapToBiMap([
      [
        GeometricProperty.HEIGHT,
        MultiPolygonAlgorithmType.POLYGON_VERTICAL_COMPONENT,
      ],
      [
        GeometricProperty.CIRCUMFERENCE,
        MultiPolygonAlgorithmType.POLYGON_PERIMETER,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MultiPolygonAlgorithmType.POLYGON_AREA,
      ],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MultiPolygonAlgorithmType.POLYGON_GROSS_SIDE_AREA,
      ],
      [
        GeometricProperty.WIDTH,
        MultiPolygonAlgorithmType.POLYGON_BOUNDING_BOX_WIDTH,
      ],
      [
        GeometricProperty.LENGTH,
        MultiPolygonAlgorithmType.POLYGON_BOUNDING_BOX_LENGTH,
      ],
      [
        LocationProperty.LOCATION_MIN_X,
        MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_X,
      ],
      [
        LocationProperty.LOCATION_MIN_Y,
        MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_Y,
      ],
      [
        LocationProperty.LOCATION_MIN_Z,
        MultiPolygonAlgorithmType.POLYGON_LOCATION_MIN_Z,
      ],
      [
        LocationProperty.LOCATION_MAX_X,
        MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_X,
      ],
      [
        LocationProperty.LOCATION_MAX_Y,
        MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_Y,
      ],
      [
        LocationProperty.LOCATION_MAX_Z,
        MultiPolygonAlgorithmType.POLYGON_LOCATION_MAX_Z,
      ],
    ]),
  ],
  [
    CustomElementClass.ExtrudedPolygonShape,
    mapToBiMap([
      [
        GeometricProperty.CIRCUMFERENCE,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_PERIMETER,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_AREA,
      ],
      [
        GeometricProperty.EXTRUDED_SIDE_AREA,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_SIDE_AREA,
      ],
      [
        GeometricProperty.VOLUME,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_VOLUME,
      ],
      [
        GeometricProperty.THICKNESS,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_THICKNESS,
      ],
      [
        GeometricProperty.WIDTH,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_BOUNDING_BOX_WIDTH,
      ],
      [
        GeometricProperty.LENGTH,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_BOUNDING_BOX_LENGTH,
      ],
      [
        LocationProperty.LOCATION_MIN_X,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_X,
      ],
      [
        LocationProperty.LOCATION_MIN_Y,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_Y,
      ],
      [
        LocationProperty.LOCATION_MIN_Z,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MIN_Z,
      ],
      [
        LocationProperty.LOCATION_MAX_X,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_X,
      ],
      [
        LocationProperty.LOCATION_MAX_Y,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_Y,
      ],
      [
        LocationProperty.LOCATION_MAX_Z,
        ExtrudedPolygonAlgorithmType.EXTRUDED_POLYGON_LOCATION_MAX_Z,
      ],
    ]),
  ],
  [
    CustomElementClass.LineShape,
    mapToBiMap([
      [GeometricProperty.LENGTH, PolylineAlgorithmType.LINE_LENGTH],
      [
        LocationProperty.LOCATION_MIN_X,
        PolylineAlgorithmType.LINE_LOCATION_MIN_X,
      ],
      [
        LocationProperty.LOCATION_MIN_Y,
        PolylineAlgorithmType.LINE_LOCATION_MIN_Y,
      ],
      [
        LocationProperty.LOCATION_MIN_Z,
        PolylineAlgorithmType.LINE_LOCATION_MIN_Z,
      ],
      [
        LocationProperty.LOCATION_MAX_X,
        PolylineAlgorithmType.LINE_LOCATION_MAX_X,
      ],
      [
        LocationProperty.LOCATION_MAX_Y,
        PolylineAlgorithmType.LINE_LOCATION_MAX_Y,
      ],
      [
        LocationProperty.LOCATION_MAX_Z,
        PolylineAlgorithmType.LINE_LOCATION_MAX_Z,
      ],
    ]),
  ],
  [
    CustomElementClass.PointShape,
    mapToBiMap([
      [LocationProperty.LOCATION_MIN_X, PointAlgorithmType.POINT_LOCATION_X],
      [LocationProperty.LOCATION_MIN_Y, PointAlgorithmType.POINT_LOCATION_Y],
      [LocationProperty.LOCATION_MIN_Z, PointAlgorithmType.POINT_LOCATION_Z],
      [LocationProperty.LOCATION_MAX_X, PointAlgorithmType.POINT_LOCATION_X],
      [LocationProperty.LOCATION_MAX_Y, PointAlgorithmType.POINT_LOCATION_Y],
      [LocationProperty.LOCATION_MAX_Z, PointAlgorithmType.POINT_LOCATION_Z],
    ]),
  ],
  [
    CustomElementClass.Polygon2Shape,
    mapToBiMap([
      [
        GeometricProperty.CIRCUMFERENCE,
        MultiPolygon2AlgorithmType.POLYGON2_PERIMETER,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MultiPolygon2AlgorithmType.POLYGON2_AREA,
      ],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MultiPolygon2AlgorithmType.POLYGON2_GROSS_SIDE_AREA,
      ],
      [
        GeometricProperty.WIDTH,
        MultiPolygon2AlgorithmType.POLYGON2_BOUNDING_BOX_WIDTH,
      ],
      [
        GeometricProperty.LENGTH,
        MultiPolygon2AlgorithmType.POLYGON2_BOUNDING_BOX_LENGTH,
      ],
      [
        LocationProperty.LOCATION_MIN_X,
        MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MIN_X,
      ],
      [
        LocationProperty.LOCATION_MIN_Y,
        MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MIN_Y,
      ],
      [
        LocationProperty.LOCATION_MAX_X,
        MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MAX_X,
      ],
      [
        LocationProperty.LOCATION_MAX_Y,
        MultiPolygon2AlgorithmType.POLYGON2_LOCATION_MAX_Y,
      ],
    ]),
  ],
  [
    CustomElementClass.Line2Shape,
    mapToBiMap([
      [GeometricProperty.LENGTH, Polyline2AlgorithmType.LINE2_LENGTH],
      [
        LocationProperty.LOCATION_MIN_X,
        Polyline2AlgorithmType.LINE2_LOCATION_MIN_X,
      ],
      [
        LocationProperty.LOCATION_MIN_Y,
        Polyline2AlgorithmType.LINE2_LOCATION_MIN_Y,
      ],
      [
        LocationProperty.LOCATION_MAX_X,
        Polyline2AlgorithmType.LINE2_LOCATION_MAX_X,
      ],
      [
        LocationProperty.LOCATION_MAX_Y,
        Polyline2AlgorithmType.LINE2_LOCATION_MAX_Y,
      ],
    ]),
  ],
  [
    CustomElementClass.Point2Shape,
    mapToBiMap([
      [LocationProperty.LOCATION_MIN_X, Point2AlgorithmType.POINT2_LOCATION_X],
      [LocationProperty.LOCATION_MIN_Y, Point2AlgorithmType.POINT2_LOCATION_Y],
      [LocationProperty.LOCATION_MAX_X, Point2AlgorithmType.POINT2_LOCATION_X],
      [LocationProperty.LOCATION_MAX_Y, Point2AlgorithmType.POINT2_LOCATION_Y],
    ]),
  ],
  [
    RevitCategory.Wall,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    RevitCategory.Window,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    RevitCategory.Slab,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.LARGEST_SURFACE_AREA_WITHOUT_OPENINGS,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    RevitCategory.CurtainWall,
    mapToBiMap([
      [
        GeometricProperty.LENGTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_WIDTH,
      ],
      [GeometricProperty.HEIGHT, MeshAlgorithmType.VERTICAL_COMPONENT],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY,
      ],
      [
        GeometricProperty.LARGEST_SIDE_AREA,
        MeshAlgorithmType.LARGEST_FLAT_SIDE_AREA,
      ],
      [GeometricProperty.CIRCUMFERENCE, MeshAlgorithmType.MESH_PERIMETER],
    ]),
  ],
  [
    RevitCategory.Beam,
    mapToBiMap([
      [GeometricProperty.LENGTH, MeshAlgorithmType.BOUNDING_BOX_LENGTH],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
      [
        GeometricProperty.GROSS_SIDE_AREA,
        MeshAlgorithmType.CONVEX_LARGEST_FLAT_SIDE_AREA_INFINITY,
      ],
    ]),
  ],
  [
    RevitCategory.Column,
    mapToBiMap([
      [GeometricProperty.LENGTH, MeshAlgorithmType.BOUNDING_BOX_LENGTH],
      [
        GeometricProperty.WIDTH,
        MeshAlgorithmType.BOUNDING_BOX_HORIZONTAL_LENGTH,
      ],
      [GeometricProperty.VOLUME, MeshAlgorithmType.TETRAHEDRON_VOLUME],
    ]),
  ],
]);

// Helper function to get the appropriate mapping for a target type
export const getMappingForTargetType = (
  targetType: AlgorithmTargetType
): BiMap<
  GeometricProperty | LocationProperty,
  GeometricAlgorithmType
> | null => {
  // Start with the default mapping for any IfcClass or RevitCategory
  let mapping: BiMap<
    GeometricProperty | LocationProperty,
    GeometricAlgorithmType
  > | null = null;
  if (
    Object.values(IfcClass).includes(targetType as IfcClass) ||
    Object.values(RevitCategory).includes(targetType as RevitCategory)
  ) {
    mapping = DEFAULT_MESH_MAPPING;
  }

  // If there's a specific mapping, merge it with the default
  const specificMapping = GEOMETRIC_PROPERTY_TO_ALGORITHM_MAP.get(targetType);
  if (specificMapping) {
    if (!mapping) {
      mapping = new BiMap<
        GeometricProperty | LocationProperty,
        GeometricAlgorithmType
      >();
    }
    // Add all entries from the specific mapping
    for (const [key, value] of specificMapping.entries()) {
      mapping.set(key, value);
    }
  }

  return mapping;
};
